import React, { useEffect, useContext } from 'react';
import classNames from 'classnames';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Card, Button } from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import PropTypes from 'prop-types';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import ProjectsContainer from './ProjectsContainer';
import ProjectsHeader from './ProjectsHeader';
import { MOBILE_BREAKPOINT } from '../constants/responsiveClassNames';
import { projectsDataModel } from './dataModel';
import { ProjectsLoading } from './ProjectsLoading';
import ProjectsError from './ProjectsError';
import ProjectsNullView from './ProjectsNullView';
import { hasExceededMaxRetries, resetRetryTracking } from '../utils/retryTracker';
import { triggerProjectsInteractionAnalytics } from '../utils/helpers';
import { useContainerWidth } from '../hooks/useContainerWidth';

const ProjectsOverview = ({
  componentId, isDashboardSnapshot, hideViewAllSuffix, fillHeight
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [containerRef, width] = useContainerWidth();
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('projects-overview.ready'); }, []);

  const { svocID } = useThdCustomer() || {};

  const { data, loading, error, refetch } = useDataModel('projects', {
    variables: {
      accountId: svocID,
      skipImages: false,
      skipNumberOfItems: false
    },
    context: { withAuth: true },
    skip: !svocID,
    ssr: false
  });

  const impressionData = {
    name: 'ProjectsOverview',
    component: 'ProjectsOverview',
    type: 'content',
    position: '',
    id: componentId || ''
  };

  const projects = data?.projects?.projects || [];
  const totalProjectsCount = data?.projects?.totalProjects || 0;
  const isNullView = !projects || projects.length === 0;

  const isCompactView = width < MOBILE_BREAKPOINT;

  // Reset retry tracking on successful data load (even if empty)
  useEffect(() => {
    if (!loading && !error) {
      resetRetryTracking();
    }
  }, [loading, error]);

  let content;

  // Loading skeleton
  if (loading || width === 0) {
    content = <ProjectsLoading />;
  } else if (error) {
    // Error state - check if max retries exceeded
    // If user has exceeded max retries, show null state as failsafe fallback
    if (hasExceededMaxRetries()) {
      // eslint-disable-next-line no-console
      console.warn('Max retry attempts exceeded. Showing null state as fallback.');
      content = (
        <ProjectsNullView
          componentId={componentId}
          isCompactView={isCompactView}
          isDashboardSnapshot={isDashboardSnapshot}
        />
      );
    } else {
      content = <ProjectsError refresh={refetch} componentId={componentId} />;
    }
  } else if (isNullView) {
    // Null view for successful load with no projects
    content = (
      <ProjectsNullView
        componentId={componentId}
        isCompactView={isCompactView}
        isDashboardSnapshot={isDashboardSnapshot}
      />
    );
  } else {
    let headerActions = [];
    if (width > 0) {
      headerActions = isMobile ? ['createNew'] : ['createNew', 'viewAll'];
    }
    const viewAllLabel = `View All Projects (${totalProjectsCount.toLocaleString()})`;
    const handleViewAllClick = () => {
      triggerProjectsInteractionAnalytics({
        action: 'clickthrough',
        component: 'view all',
        id: componentId,
      });
    };
    content = (
      <div
        data-component="ProjectsOverview"
        data-testid="projects-overview"
        className={classNames('sui-flex sui-flex-col sui-h-full', { 'sui-gap-4': !isDashboardSnapshot })}
      >
        {!isDashboardSnapshot && (
          <ProjectsHeader
            projectCount={totalProjectsCount}
            actions={headerActions}
            componentId={componentId}
            hideViewAllSuffix={hideViewAllSuffix}
          />
        )}
        {/*
          Matches the Figma "Carousel" spec: 24px top/bottom around the row of cards, desktop
          only (mobile has its own compact-list layout with no equivalent spec). No left/right
          padding here — the shared 16px horizontal inset already comes from
          ProAlertsAndUpdates' own wrapper (the same one the tab bar and Footer button align
          to), so adding another layer here stacked to 32px and threw the cards out of
          alignment with them. Isolated to its own wrapper rather than folded into the outer
          Card padding, since the Footer below has a different padding shape of its own — see
          below.

          fillHeight grows this block into whatever height the host gives the card, so the
          carousel (and its cards) fill it instead of leaving the space below the Footer.
        */}
        <div className={classNames({
          'sui-pt-6 sui-pb-6': isDashboardSnapshot && !isMobile,
          'sui-grow sui-flex sui-flex-col': fillHeight
        })}
        >
          <ProjectsContainer
            projects={projects}
            maxMobileProjectSize={3}
            totalProjectsCount={totalProjectsCount}
            iconSize="small"
            isCompactView={isMobile}
            isDashboardSnapshot={isDashboardSnapshot}
            fillHeight={fillHeight}
          />
        </div>
        {isDashboardSnapshot && (
          // Matches the Figma "Footer" spec: 24px bottom padding (the space below the
          // button before the card's own edge), 16px right padding on the desktop button
          // only (it's right-aligned via justify-end; the mobile button is fullWidth and
          // has no equivalent right inset in spec).
          //
          // sui-block sm:sui-hidden / sui-hidden sm:sui-block (a real viewport breakpoint),
          // not showMobileLayout/showDesktopLayout (a container query against this card's
          // own width): the card's own width can cross the container query's 384px
          // threshold at a different point than the page's real viewport does, so this
          // button was flipping to the desktop/outline variant at a different width than
          // Orders' own View All button (which already uses a viewport breakpoint) —
          // visibly inconsistent side by side in the same Dashboard Snapshot. Matching
          // Orders' mechanism keeps all four tabs switching at the same point.
          <div data-testid="projects-snapshot-view-all" className="sui-pb-6">
            <div className="sui-block sm:sui-hidden">
              <Button href="/projects" onClick={handleViewAllClick} fullWidth>{viewAllLabel}</Button>
            </div>
            <div className="sui-hidden sm:sui-block">
              <div className="sui-flex sui-justify-end sui-pr-4">
                <Button href="/projects" onClick={handleViewAllClick} variant="secondary">{viewAllLabel}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <ImpressionProvider data={impressionData}>
      <Card
        className={classNames('sui-h-full', { 'sui-rounded-md': isDashboardSnapshot })}
        // Dashboard Snapshot already supplies a single 16px inset around the active tab's
        // panel (split across ProAlertsAndUpdates' and SnapshotTabView's own wrappers, never
        // doubled between them). This Card's own sui-p-4 is correct for standalone usage
        // (Account Dashboard, etc.), where it's the only inset — but inside the snapshot it
        // stacked a second, redundant 16px on all four sides on top of the shared one. Your
        // Latest Orders has no Card of its own here and never had this extra layer, which is
        // why it was the one tab actually matching spec (flagged by both QE and UX).
        CardContentProps={{
          className: classNames(
            'sui-flex sui-overflow-hidden sui-gap-4 sui-flex-col sui-w-full sui-h-full',
            { 'sui-p-4': !isDashboardSnapshot }
          ),
        }}
        PaperProps={isDashboardSnapshot ? { variant: 'shadow', shadow: 'none' } : undefined}
      >
        <div ref={containerRef} className="sui-@container sui-h-full">
          {content}
        </div>
      </Card>
    </ImpressionProvider>
  );
};

ProjectsOverview.propTypes = {
  componentId: PropTypes.string,
  /**
   * Set true when rendering inside Dashboard Snapshot. Suppresses this component's own
   * "Projects (n)" title and Create New action, and drops the card border, since the
   * snapshot's tabs and panel supply those. View All still renders, moved below the cards
   * and labeled with the project count.
   */
  isDashboardSnapshot: PropTypes.bool,
  /**
   * Renders the header's View All action as just "View All" rather than "View All Projects",
   * for consumers that already label the card (the account dashboard does). HEADER ONLY - the
   * compact-view button keeps its full label, since mobile has no card title beside it to
   * supply the noun. Dashboard Snapshot's counted View All is also unaffected.
   */
  hideViewAllSuffix: PropTypes.bool,
  /**
   * Stretches the project cards to the bottom of the card, for consumers that size the card
   * from outside (Dashboard Snapshot matches its row neighbor's height). Off by default:
   * standalone, the extra height is just empty space. In Dashboard Snapshot it also replaces
   * the cards' 29rem floor: the snapshot sets the height, and the cards fill it.
   */
  fillHeight: PropTypes.bool,
};

ProjectsOverview.defaultProps = {
  componentId: '',
  isDashboardSnapshot: false,
  hideViewAllSuffix: false,
  fillHeight: false,
};

ProjectsOverview.displayName = 'ProjectsOverview';

ProjectsOverview.dataModel = projectsDataModel;

export { ProjectsOverview };
